/**
 * trigger-form.tsx — Create/edit form for escalation trigger configuration.
 *
 * AC 4, 5, 6, 7, 8:
 * - Type-discriminated fields rendered per `type` selection
 * - cluster type: multi-chip symptom list + N-of-M integer input (min=2)
 * - delta type: deltaType radio, unit field hidden for relative_percent,
 *   timeWindowDays optional with placeholder "blank = from enrollment baseline"
 * - md tier: save blocked until MdTierConfirmationDialog confirmed
 * - cooldown field defaults to 24, shows "hours" unit label
 */

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import type {
  EscalationDestinationTier,
  EscalationTriggerConfig,
  EscalationTriggerRecord,
  EscalationTriggerType,
} from './escalation-params-api.js';
import { MdTierConfirmationDialog } from './md-tier-confirmation-dialog.js';

interface TriggerFormProps {
  initial?: EscalationTriggerRecord;
  onSave: (
    data: {
      name: string;
      type: EscalationTriggerType;
      config: EscalationTriggerConfig;
      destinationTier: EscalationDestinationTier;
      cooldownHours: number;
      mdTierConfirmed?: boolean;
    },
    triggerId?: string,
  ) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveError: string | null;
}

export function TriggerForm({
  initial,
  onSave,
  onCancel,
  saving,
  saveError,
}: TriggerFormProps): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<EscalationTriggerType>(initial?.type ?? 'threshold');
  const [destinationTier, setDestinationTier] = useState<EscalationDestinationTier>(
    initial?.destinationTier ?? 'rd',
  );
  const [cooldownHours, setCooldownHours] = useState(String(initial?.cooldownHours ?? 24));

  // MD tier confirmation state.
  // An existing md-tier trigger is treated as already-confirmed for its CURRENT
  // safety-relevant config, so a rename or cooldown tweak does not re-demand
  // confirmation. The confirmation is invalidated as soon as the safety-relevant
  // config (matching config body + destination tier) changes — see the effect
  // below — so a config change after a confirm (or after a failed save) cannot
  // ride on a stale confirmation flag.
  const [showMdDialog, setShowMdDialog] = useState(false);
  const initialMdConfirmed = (initial?.destinationTier ?? 'rd') === 'md';
  const [mdTierConfirmed, setMdTierConfirmed] = useState(initialMdConfirmed);

  // Threshold config
  const [thresholdSourceField, setThresholdSourceField] = useState(
    initial?.config.type === 'threshold' ? initial.config.sourceField : '',
  );
  const [thresholdComparator, setThresholdComparator] = useState<'>=' | '<=' | '>' | '<'>(
    initial?.config.type === 'threshold' ? initial.config.comparator : '>=',
  );
  const [thresholdValue, setThresholdValue] = useState(
    initial?.config.type === 'threshold' ? String(initial.config.value) : '',
  );
  const [thresholdUnit, setThresholdUnit] = useState(
    initial?.config.type === 'threshold' ? initial.config.unit : '',
  );

  // Delta config
  const [deltaSourceField, setDeltaSourceField] = useState(
    initial?.config.type === 'delta' ? initial.config.sourceField : '',
  );
  const [deltaDirection, setDeltaDirection] = useState<'increase' | 'decrease'>(
    initial?.config.type === 'delta' ? initial.config.direction : 'increase',
  );
  const [deltaDeltaType, setDeltaDeltaType] = useState<'absolute' | 'relative_percent'>(
    initial?.config.type === 'delta' ? initial.config.deltaType : 'absolute',
  );
  const [deltaMagnitude, setDeltaMagnitude] = useState(
    initial?.config.type === 'delta' ? String(initial.config.magnitude) : '',
  );
  const [deltaUnit, setDeltaUnit] = useState(
    initial?.config.type === 'delta' ? (initial.config.unit ?? '') : '',
  );
  const [deltaTimeWindowDays, setDeltaTimeWindowDays] = useState(
    initial?.config.type === 'delta'
      ? initial.config.timeWindowDays != null
        ? String(initial.config.timeWindowDays)
        : ''
      : '',
  );

  // Behavioral config
  const [behaviorType, setBehaviorType] = useState(
    initial?.config.type === 'behavioral' ? initial.config.behaviourType : '',
  );
  const [behaviorConsecutiveDays, setBehaviorConsecutiveDays] = useState(
    initial?.config.type === 'behavioral' ? String(initial.config.consecutiveDaysThreshold) : '1',
  );

  // SDOH config
  const [sdohFlagType, setSdohFlagType] = useState<'food_access' | 'financial_barrier'>(
    initial?.config.type === 'sdoh' ? initial.config.flagType : 'food_access',
  );

  // Cluster config
  const [clusterSymptoms, setClusterSymptoms] = useState<string[]>(
    initial?.config.type === 'cluster' ? [...initial.config.symptomList] : [],
  );
  const [clusterNewSymptom, setClusterNewSymptom] = useState('');
  const [clusterMinCount, setClusterMinCount] = useState(
    initial?.config.type === 'cluster' ? String(initial.config.minimumCoOccurrenceCount) : '2',
  );

  // Keyword config
  const [keywordPhrase, setKeywordPhrase] = useState(
    initial?.config.type === 'keyword' ? initial.config.phrase : '',
  );

  function buildConfig(): EscalationTriggerConfig | null {
    switch (type) {
      case 'threshold':
        if (!thresholdSourceField || !thresholdValue || !thresholdUnit) return null;
        return {
          type: 'threshold',
          sourceField: thresholdSourceField,
          comparator: thresholdComparator,
          value: parseFloat(thresholdValue),
          unit: thresholdUnit,
        };
      case 'delta':
        if (!deltaSourceField || !deltaMagnitude) return null;
        return {
          type: 'delta',
          sourceField: deltaSourceField,
          direction: deltaDirection,
          deltaType: deltaDeltaType,
          magnitude: parseFloat(deltaMagnitude),
          unit: deltaDeltaType === 'relative_percent' ? null : deltaUnit || null,
          timeWindowDays: deltaTimeWindowDays ? parseFloat(deltaTimeWindowDays) : null,
        };
      case 'behavioral':
        if (!behaviorType || !behaviorConsecutiveDays) return null;
        return {
          type: 'behavioral',
          behaviourType: behaviorType,
          consecutiveDaysThreshold: parseInt(behaviorConsecutiveDays, 10),
        };
      case 'sdoh':
        return { type: 'sdoh', flagType: sdohFlagType };
      case 'cluster':
        if (clusterSymptoms.length < 2) return null;
        return {
          type: 'cluster',
          symptomList: clusterSymptoms,
          minimumCoOccurrenceCount: parseInt(clusterMinCount, 10),
        };
      case 'keyword':
        if (!keywordPhrase) return null;
        return { type: 'keyword', phrase: keywordPhrase };
      default:
        return null;
    }
  }

  // Safety-relevant signature: the destination tier plus the matching config body.
  // A change here invalidates any prior MD confirmation (incl. after a failed
  // save) so the gate is re-shown. A non-safety change (name, cooldown) does not
  // appear here and so does not invalidate the confirmation.
  const safetySignature = JSON.stringify({ destinationTier, config: buildConfig() });
  const confirmedSignatureRef = useRef<string>(
    initialMdConfirmed ? JSON.stringify({ destinationTier, config: buildConfig() }) : '',
  );

  useEffect(() => {
    if (mdTierConfirmed && safetySignature !== confirmedSignatureRef.current) {
      // Safety-relevant config changed after confirmation — invalidate it.
      setMdTierConfirmed(false);
    }
  }, [safetySignature, mdTierConfirmed]);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const config = buildConfig();
    if (!config) return;

    if (destinationTier === 'md' && !mdTierConfirmed) {
      setShowMdDialog(true);
      return;
    }

    void onSave(
      {
        name,
        type,
        config,
        destinationTier,
        cooldownHours: parseInt(cooldownHours, 10) || 24,
        ...(destinationTier === 'md' ? { mdTierConfirmed } : {}),
      },
      initial?.id,
    );
  }

  function handleMdConfirm(): void {
    const config = buildConfig();
    if (!config) return;
    // Bind the confirmation to the exact config being approved. The effect above
    // compares the live signature to this baseline and re-shows the gate if the
    // safety-relevant config changes afterwards.
    confirmedSignatureRef.current = JSON.stringify({ destinationTier, config });
    setMdTierConfirmed(true);
    setShowMdDialog(false);
    void onSave(
      {
        name,
        type,
        config,
        destinationTier,
        cooldownHours: parseInt(cooldownHours, 10) || 24,
        mdTierConfirmed: true,
      },
      initial?.id,
    );
  }

  return (
    <>
      <MdTierConfirmationDialog
        open={showMdDialog}
        onConfirm={handleMdConfirm}
        onCancel={() => setShowMdDialog(false)}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="e.g. LDL-C High Threshold"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as EscalationTriggerType)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="threshold">Threshold — absolute value comparison</option>
            <option value="delta">Delta — change from baseline</option>
            <option value="behavioral">Behavioral — dietary/adherence non-compliance</option>
            <option value="sdoh">SDOH — social determinant flag</option>
            <option value="cluster">Cluster — symptom co-occurrence</option>
            <option value="keyword">Keyword — chat keyword/phrase match</option>
          </select>
        </div>

        {/* Type-specific config fields */}
        {type === 'threshold' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Threshold Config</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Source Field</label>
                <input
                  type="text"
                  value={thresholdSourceField}
                  onChange={(e) => setThresholdSourceField(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="e.g. ldl_c"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Comparator</label>
                <select
                  value={thresholdComparator}
                  onChange={(e) =>
                    setThresholdComparator(e.target.value as typeof thresholdComparator)
                  }
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value=">=">≥</option>
                  <option value="<=">≤</option>
                  <option value=">">{'>'}</option>
                  <option value="<">{'<'}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Value</label>
                <input
                  type="number"
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Unit</label>
                <input
                  type="text"
                  value={thresholdUnit}
                  onChange={(e) => setThresholdUnit(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="e.g. mg/dL"
                />
              </div>
            </div>
          </div>
        )}

        {type === 'delta' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Delta Config</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Source Field</label>
                <input
                  type="text"
                  value={deltaSourceField}
                  onChange={(e) => setDeltaSourceField(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Direction</label>
                <select
                  value={deltaDirection}
                  onChange={(e) => setDeltaDirection(e.target.value as typeof deltaDirection)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="increase">Increase</option>
                  <option value="decrease">Decrease</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Delta Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    value="absolute"
                    checked={deltaDeltaType === 'absolute'}
                    onChange={() => setDeltaDeltaType('absolute')}
                  />
                  Absolute
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    value="relative_percent"
                    checked={deltaDeltaType === 'relative_percent'}
                    onChange={() => setDeltaDeltaType('relative_percent')}
                  />
                  Relative Percent
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Magnitude</label>
                <input
                  type="number"
                  value={deltaMagnitude}
                  onChange={(e) => setDeltaMagnitude(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              {/* Unit hidden for relative_percent */}
              {deltaDeltaType === 'absolute' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Unit</label>
                  <input
                    type="text"
                    value={deltaUnit}
                    onChange={(e) => setDeltaUnit(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    placeholder="e.g. mg/dL"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Time Window (days)</label>
              <input
                type="number"
                value={deltaTimeWindowDays}
                onChange={(e) => setDeltaTimeWindowDays(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="blank = from enrollment baseline"
              />
            </div>
          </div>
        )}

        {type === 'behavioral' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Behavioral Config</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Behaviour Type</label>
                <input
                  type="text"
                  value={behaviorType}
                  onChange={(e) => setBehaviorType(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="e.g. missed_check_in"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Consecutive Days</label>
                <input
                  type="number"
                  min="1"
                  value={behaviorConsecutiveDays}
                  onChange={(e) => setBehaviorConsecutiveDays(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {type === 'sdoh' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">SDOH Config</p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Flag Type</label>
              <select
                value={sdohFlagType}
                onChange={(e) => setSdohFlagType(e.target.value as typeof sdohFlagType)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="food_access">Food Access</option>
                <option value="financial_barrier">Financial Barrier</option>
              </select>
            </div>
          </div>
        )}

        {type === 'cluster' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Cluster Config</p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Symptom List (min 2 symptoms)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {clusterSymptoms.map((symptom, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                  >
                    {symptom}
                    <button
                      type="button"
                      onClick={() =>
                        setClusterSymptoms((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      className="text-blue-600 hover:text-blue-900"
                      aria-label={`Remove ${symptom}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={clusterNewSymptom}
                  onChange={(e) => setClusterNewSymptom(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (clusterNewSymptom.trim()) {
                        setClusterSymptoms((prev) => [...prev, clusterNewSymptom.trim()]);
                        setClusterNewSymptom('');
                      }
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="Type symptom and press Enter"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (clusterNewSymptom.trim()) {
                      setClusterSymptoms((prev) => [...prev, clusterNewSymptom.trim()]);
                      setClusterNewSymptom('');
                    }
                  }}
                  className="px-3 py-1 text-sm bg-gray-100 border border-gray-300 rounded"
                >
                  Add
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Minimum co-occurrence (N of M)
              </label>
              <input
                type="number"
                min="2"
                value={clusterMinCount}
                onChange={(e) => setClusterMinCount(e.target.value)}
                required
                className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
        )}

        {type === 'keyword' && (
          <div className="space-y-3 border border-gray-200 rounded p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Keyword Config</p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Phrase</label>
              <input
                type="text"
                value={keywordPhrase}
                onChange={(e) => setKeywordPhrase(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="e.g. suicidal thoughts"
              />
            </div>
          </div>
        )}

        {/* Destination Tier */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Destination Tier</label>
          <select
            value={destinationTier}
            onChange={(e) => {
              setDestinationTier(e.target.value as EscalationDestinationTier);
              if (e.target.value !== 'md') {
                setMdTierConfirmed(false);
              }
            }}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="wa">WA — Workforce Assistance ops queue</option>
            <option value="rd">RD — Registered Dietitian queue</option>
            <option value="md">MD — Medical Director (requires confirmation)</option>
            <option value="emergency">Emergency — 911 + simultaneous alert chain</option>
          </select>
        </div>

        {/* Cooldown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Post-resolution cooldown
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="720"
              value={cooldownHours}
              onChange={(e) => setCooldownHours(e.target.value)}
              className="w-32 border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <span className="text-sm text-gray-600">hours</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Default: 24 hours. Range: 1–720 hours (30 days).
          </p>
        </div>

        {saveError && <p className="text-red-600 text-sm">{saveError}</p>}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="trigger-form-save"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Trigger'}
          </button>
        </div>
      </form>
    </>
  );
}
